import { useEffect, useState } from "react";
import { X, Sparkles } from "lucide-react";

const STORAGE_KEY = "blendsafe_announcement_dismissed_v1";

/**
 * AnnouncementBar — slim, premium top bar above the header.
 *
 * • Dismissal persists in localStorage (one-time dismiss, no nagging).
 * • Forest-green warm tone matches the wordmark, never screaming.
 * • Only renders when founding spots are still available (controlled via
 *   the `enabled` prop driven by the parent's waitlist count check).
 */
export default function AnnouncementBar({ enabled = true }) {
  const [dismissed, setDismissed] = useState(true); // start dismissed so SSR/SEO doesn't render it
  const [animatingOut, setAnimatingOut] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    try {
      const was = localStorage.getItem(STORAGE_KEY);
      if (!was) setDismissed(false);
    } catch {
      setDismissed(false);
    }
  }, [enabled]);

  const onDismiss = () => {
    setAnimatingOut(true);
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* ignore */ }
    setTimeout(() => setDismissed(true), 280);
  };

  if (!enabled || dismissed) return null;

  return (
    <div
      data-testid="announcement-bar"
      className={[
        "relative z-[60] overflow-hidden transition-all duration-300 ease-out",
        animatingOut ? "max-h-0 opacity-0" : "max-h-16 opacity-100",
      ].join(" ")}
    >
      <div
        className="relative text-cream-50"
        style={{
          background: "linear-gradient(90deg, #1f3d2e 0%, #29503e 50%, #1f3d2e 100%)",
        }}
      >
        {/* Soft glow accent */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-25"
          style={{
            background: "radial-gradient(ellipse at center, rgba(232,105,58,0.5) 0%, transparent 60%)",
          }}
        />
        <div className="relative max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-10 h-10 sm:h-11 flex items-center justify-between gap-3">
          <p className="flex items-center gap-2 text-[12px] sm:text-[13px] font-medium tracking-[0.005em]">
            <Sparkles className="w-3.5 h-3.5 text-[#e8a36a] shrink-0" />
            <span>
              Become a founding member —
              <span className="ml-1.5 font-semibold">
                Pro free for 12 months when payments launch
              </span>
            </span>
          </p>
          <button
            type="button"
            data-testid="announcement-dismiss"
            onClick={onDismiss}
            aria-label="Dismiss announcement"
            className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full hover:bg-white/10 transition-colors text-cream-50/80 hover:text-cream-50"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
